if (!customElements.get('wm-upsell-gallery')) {
  customElements.define(
    'wm-upsell-gallery',
    class WmUpsellGallery extends HTMLElement {
      constructor() {
        super();

        this.mainImage = null;
        this.buttons = [];
        this.thumbs = null;
        this.stage = null;
        this.scrollButton = null;

        this.unsubscribeVariantChange = null;
        this.resizeObserver = null;

        this.handleThumbClick = this.handleThumbClick.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleThumbScroll = this.handleThumbScroll.bind(this);
        this.handleScrollButton = this.handleScrollButton.bind(this);
        this.updateScrollButton = this.updateScrollButton.bind(this);
      }

      connectedCallback() {
        this.mainImage = this.querySelector('[data-wm-main-image]');

        this.buttons = Array.from(
          this.querySelectorAll('[data-wm-thumb]')
        );

        this.thumbs = this.querySelector(
          '.wm-upsell-gallery__thumbs'
        );

        this.stage = this.querySelector(
          '.wm-upsell-gallery__stage'
        );


        /* =====================================================
           THUMBNAIL KLICKS
           ===================================================== */

        this.buttons.forEach((button) => {
          button.addEventListener(
            'click',
            this.handleThumbClick
          );
        });


        /* =====================================================
           SHOPIFY VARIANTENWECHSEL
           ===================================================== */

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.unsubscribeVariantChange = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange
          );
        }


        /* =====================================================
           DESKTOP SCROLL BUTTON
           ===================================================== */

        this.setupScrollButton();


        /* =====================================================
           RESIZE BEOBACHTEN
           ===================================================== */

        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(() => {
            this.updateScrollButton();
          });

          if (this.thumbs) {
            this.resizeObserver.observe(this.thumbs);
          }

          if (this.stage) {
            this.resizeObserver.observe(this.stage);
          }
        }


        window.addEventListener(
          'resize',
          this.updateScrollButton
        );


        requestAnimationFrame(() => {
          this.updateScrollButton();
        });
      }


      disconnectedCallback() {
        this.buttons.forEach((button) => {
          button.removeEventListener(
            'click',
            this.handleThumbClick
          );
        });


        if (this.thumbs) {
          this.thumbs.removeEventListener(
            'scroll',
            this.handleThumbScroll
          );
        }


        if (this.scrollButton) {
          this.scrollButton.removeEventListener(
            'click',
            this.handleScrollButton
          );
        }


        if (this.unsubscribeVariantChange) {
          this.unsubscribeVariantChange();
        }


        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }


        window.removeEventListener(
          'resize',
          this.updateScrollButton
        );
      }


      /* =======================================================
         SCROLL BUTTON AUFBAUEN
         ======================================================= */

      setupScrollButton() {
        if (!this.thumbs) return;

        const existingButton = this.querySelector(
          '[data-wm-gallery-scroll]'
        );

        if (existingButton) {
          this.scrollButton = existingButton;
        } else {
          this.scrollButton = document.createElement('button');

          this.scrollButton.type = 'button';

          this.scrollButton.className =
            'wm-upsell-gallery__scroll-button';

          this.scrollButton.setAttribute(
            'data-wm-gallery-scroll',
            ''
          );

          this.scrollButton.setAttribute(
            'aria-label',
            'Weitere Produktbilder anzeigen'
          );

          this.scrollButton.innerHTML = `
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          `;

          this.appendChild(this.scrollButton);
        }


        this.scrollButton.addEventListener(
          'click',
          this.handleScrollButton
        );


        this.thumbs.addEventListener(
          'scroll',
          this.handleThumbScroll,
          {
            passive: true
          }
        );
      }


      /* =======================================================
         SCROLL BUTTON KLICK
         ======================================================= */

      handleScrollButton() {
        if (!this.thumbs) return;

        const scrollAmount = 222;

        this.thumbs.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        });
      }


      /* =======================================================
         SCROLL STATUS
         ======================================================= */

      handleThumbScroll() {
        this.updateScrollButton();
      }


      updateScrollButton() {
        if (
          !this.thumbs ||
          !this.scrollButton
        ) {
          return;
        }


        /* Mobile: Pfeil nicht nötig */

        if (
          window.matchMedia(
            '(max-width: 989px)'
          ).matches
        ) {
          this.scrollButton.hidden = true;

          return;
        }


        const hasOverflow =
          this.thumbs.scrollHeight >
          this.thumbs.clientHeight + 3;


        const isAtBottom =
          this.thumbs.scrollTop +
            this.thumbs.clientHeight >=
          this.thumbs.scrollHeight - 5;


        this.scrollButton.hidden =
          !hasOverflow || isAtBottom;
      }


      /* =======================================================
         THUMBNAIL KLICK
         ======================================================= */

      handleThumbClick(event) {
        const button = event.currentTarget;

        this.activate(button);
      }


      /* =======================================================
         VARIANTENWECHSEL
         ======================================================= */

      handleVariantChange(event) {
        const data = event?.data;

        if (!data) return;


        if (
          String(data.sectionId) !==
          String(this.dataset.section)
        ) {
          return;
        }


        const mediaId =
          data.variant?.featured_media?.id;


        if (!mediaId) return;


        const button = this.buttons.find(
          (item) =>
            String(item.dataset.mediaId) ===
            String(mediaId)
        );


        if (button) {
          this.activate(button);
        }
      }


      /* =======================================================
         HAUPTBILD WECHSELN
         ======================================================= */

      activate(button) {
        if (
          !button ||
          !this.mainImage
        ) {
          return;
        }


        const src = button.dataset.src;
        const alt = button.dataset.alt || '';


        if (!src) return;


        this.mainImage.classList.add(
          'is-changing'
        );


        const preload = new Image();


        preload.onload = () => {
          this.mainImage.src = src;

          this.mainImage.removeAttribute(
            'srcset'
          );

          this.mainImage.alt = alt;


          this.buttons.forEach((item) => {
            const active =
              item === button;


            item.classList.toggle(
              'is-active',
              active
            );


            if (active) {
              item.setAttribute(
                'aria-current',
                'true'
              );
            } else {
              item.removeAttribute(
                'aria-current'
              );
            }
          });


          button.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });


          requestAnimationFrame(() => {
            this.mainImage.classList.remove(
              'is-changing'
            );
          });


          setTimeout(() => {
            this.updateScrollButton();
          }, 180);
        };


        preload.src = src;
      }
    }
  );
}


/* ============================================================
   MOBILE SWIPE + BILDZÄHLER
   - nur Galerie
   - keine Änderung an Desktop-Layout oder bestehender Logik
   ============================================================ */

(() => {
  const mobileGalleryQuery =
    window.matchMedia('(max-width: 989px)');

  const styleId =
    'wm-upsell-gallery-mobile-swipe-style';


  if (!document.getElementById(styleId)) {
    const style =
      document.createElement('style');

    style.id = styleId;

    style.textContent = `
      .wm-upsell-gallery__counter {
        display: none;
      }

      @media screen and (max-width: 989px) {

        .wm-upsell-gallery__stage {
          touch-action: pan-y pinch-zoom;
        }

        .wm-upsell-gallery__counter {
          position: absolute;
          z-index: 2 !important;

          right: 11px;
          bottom: 11px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          min-width: 46px;
          height: 28px;

          padding: 0 9px;

          border:
            1px solid
            rgba(255, 255, 255, 0.18);

          border-radius: 999px;

          background:
            rgba(31, 35, 38, 0.68);

          color:
            rgba(245, 243, 239, 0.94);

          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1;
          letter-spacing: 0.02em;

          pointer-events: none;
        }
      }
    `;

    document.head.appendChild(style);
  }


  const setupGallery = (gallery) => {
    if (
      !gallery ||
      gallery.dataset.wmMobileSwipeReady === 'true'
    ) {
      return;
    }


    const stage =
      gallery.querySelector(
        '.wm-upsell-gallery__stage'
      );


    const buttons =
      Array.from(
        gallery.querySelectorAll(
          '[data-wm-thumb]'
        )
      );


    if (
      !stage ||
      buttons.length <= 1
    ) {
      return;
    }


    gallery.dataset.wmMobileSwipeReady =
      'true';


    const counter =
      document.createElement('span');


    counter.className =
      'wm-upsell-gallery__counter';


    counter.setAttribute(
      'data-wm-gallery-counter',
      ''
    );


    counter.setAttribute(
      'aria-hidden',
      'true'
    );


    stage.appendChild(counter);


    const updateCounter = () => {
      const activeIndex =
        buttons.findIndex(
          (button) =>
            button.classList.contains(
              'is-active'
            )
        );


      const currentIndex =
        activeIndex >= 0
          ? activeIndex
          : 0;


      counter.textContent =
        `${currentIndex + 1} / ${buttons.length}`;
    };


    updateCounter();


    const observer =
      new MutationObserver(
        updateCounter
      );


    buttons.forEach((button) => {
      observer.observe(
        button,
        {
          attributes: true,
          attributeFilter: ['class']
        }
      );
    });


    let startX = 0;
    let startY = 0;
    let trackingTouch = false;


    stage.addEventListener(
      'touchstart',
      (event) => {

        if (
          !mobileGalleryQuery.matches ||
          event.touches.length !== 1
        ) {
          trackingTouch = false;

          return;
        }


        const touch =
          event.touches[0];


        startX =
          touch.clientX;


        startY =
          touch.clientY;


        trackingTouch =
          true;
      },
      {
        passive: true
      }
    );


    stage.addEventListener(
      'touchend',
      (event) => {

        if (
          !trackingTouch ||
          !mobileGalleryQuery.matches ||
          event.changedTouches.length !== 1
        ) {
          trackingTouch = false;

          return;
        }


        const touch =
          event.changedTouches[0];


        const deltaX =
          touch.clientX - startX;


        const deltaY =
          touch.clientY - startY;


        trackingTouch =
          false;


        if (
          Math.abs(deltaX) < 45 ||
          Math.abs(deltaX) <=
            Math.abs(deltaY) * 1.15
        ) {
          return;
        }


        const activeIndex =
          buttons.findIndex(
            (button) =>
              button.classList.contains(
                'is-active'
              )
          );


        const currentIndex =
          activeIndex >= 0
            ? activeIndex
            : 0;


        const direction =
          deltaX < 0
            ? 1
            : -1;


        const targetIndex =
          currentIndex + direction;


        if (
          targetIndex < 0 ||
          targetIndex >= buttons.length
        ) {
          return;
        }


        if (
          typeof gallery.activate ===
          'function'
        ) {

          gallery.activate(
            buttons[targetIndex]
          );

        } else {

          buttons[targetIndex].click();

        }
      },
      {
        passive: true
      }
    );


    stage.addEventListener(
      'touchcancel',
      () => {
        trackingTouch = false;
      },
      {
        passive: true
      }
    );
  };


  const setupAllGalleries = (
    root = document
  ) => {

    root
      .querySelectorAll(
        'wm-upsell-gallery'
      )
      .forEach(
        setupGallery
      );
  };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        setupAllGalleries();
      },
      {
        once: true
      }
    );

  } else {

    setupAllGalleries();

  }


  document.addEventListener(
    'shopify:section:load',
    (event) => {

      setupAllGalleries(
        event.target
      );

    }
  );
})();



/* ============================================================
   WOOD & MORE – EINHEITLICHE KAUF-HIERARCHIE

   Gilt für ALLE Produkte mit dem
   wm-upsell Produkttemplate.

   Reihenfolge:

   Eyebrow
   Titel
   Bewertung
   Preis
   Steuer / Versand
   Verfügbarkeit
   Lieferzeit
   Teaser
   Varianten
   Optionen
   Menge / Warenkorb

   WICHTIG:
   - nur vorhandene DOM-Elemente werden verschoben
   - keine Preislogik
   - keine Variantenlogik
   - keine Warenkorblogik
   - keine Checkout-Logik
   ============================================================ */

(() => {

  if (
    window.__wmUpsellBuyOrderReady
  ) {
    return;
  }


  window.__wmUpsellBuyOrderReady =
    true;


  const productInfoSelector =
    'product-info.wm-upsell-product, product-info[id^="MainProduct"]';



  /* ==========================================================
     EINZELNES ELEMENT SICHER POSITIONIEREN
     ========================================================== */

  const placeAfter = (
    anchorElement,
    element
  ) => {

    if (
      !anchorElement ||
      !element
    ) {
      return anchorElement;
    }


    /*
     * Nur verschieben,
     * wenn das Element noch nicht
     * an der richtigen Stelle steht.
     *
     * Wichtig für MutationObserver:
     * verhindert unnötige DOM-Schleifen.
     */

    if (
      anchorElement.nextElementSibling
      !== element
    ) {

      anchorElement.insertAdjacentElement(
        'afterend',
        element
      );

    }


    return element;
  };



  /* ==========================================================
     KAUFBEREICH NORMALISIEREN
     ========================================================== */

  const normalizeProductInfo = (
    productInfo
  ) => {

    if (!productInfo) {
      return;
    }


    const price =
      productInfo.querySelector(
        '.wm-upsell-product__price'
      );


    const tax =
      productInfo.querySelector(
        '.wm-upsell-product__tax'
      );


    const inventory =
      productInfo.querySelector(
        '.wm-upsell-product__inventory'
      );


    const delivery =
      productInfo.querySelector(
        '.wm-upsell-product__delivery'
      );


    const teaser =
      productInfo.querySelector(
        '.wm-upsell-product__teaser'
      );


    /*
     * Varianten werden bewusst NICHT
     * direkt verschoben.
     *
     * Sie liegen im Template bereits
     * hinter Preis bzw. Titelbereich.
     *
     * Wenn Inventory, Delivery und Teaser
     * vor die Varianten geschoben werden,
     * ergibt sich automatisch:
     *
     * Preis
     * Verfügbarkeit
     * Lieferzeit
     * Teaser
     * Varianten
     */


    const priceInfoAnchor =
      tax || price;


    if (!priceInfoAnchor) {
      return;
    }


    let anchor =
      priceInfoAnchor;


    if (inventory) {

      anchor =
        placeAfter(
          anchor,
          inventory
        );

    }


    if (delivery) {

      anchor =
        placeAfter(
          anchor,
          delivery
        );

    }


    if (teaser) {

      placeAfter(
        anchor,
        teaser
      );

    }
  };



  /* ==========================================================
     ALLE WM-UPSELL PRODUKTE IM BEREICH
     ========================================================== */

  const normalizeBuyArea = (
    scope = document
  ) => {

    const productInfos = [];


    if (
      scope &&
      typeof scope.matches ===
        'function' &&
      scope.matches(
        productInfoSelector
      )
    ) {

      productInfos.push(
        scope
      );

    }


    if (
      scope &&
      typeof scope.querySelectorAll ===
        'function'
    ) {

      scope
        .querySelectorAll(
          productInfoSelector
        )
        .forEach(
          (productInfo) => {

            if (
              !productInfos.includes(
                productInfo
              )
            ) {

              productInfos.push(
                productInfo
              );

            }

          }
        );

    }


    productInfos.forEach(
      normalizeProductInfo
    );
  };



  /* ==========================================================
     NORMALISIERUNG IM NÄCHSTEN FRAME
     ========================================================== */

  const runNormalize = (
    scope = document
  ) => {

    window.requestAnimationFrame(
      () => {

        normalizeBuyArea(
          scope
        );

      }
    );
  };



  /* ==========================================================
     INITIALER SEITENAUFRUF
     ========================================================== */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {

        runNormalize(
          document
        );

      },
      {
        once: true
      }
    );

  } else {

    runNormalize(
      document
    );

  }



  /* ==========================================================
     SHOPIFY THEME EDITOR
     ========================================================== */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      runNormalize(
        event.target
      );

    }
  );



  /* ==========================================================
     SHOPIFY VARIANTENWECHSEL
     ========================================================== */

  if (
    typeof subscribe ===
      'function' &&
    typeof PUB_SUB_EVENTS !==
      'undefined' &&
    PUB_SUB_EVENTS.variantChange
  ) {

    subscribe(
      PUB_SUB_EVENTS.variantChange,
      () => {

        window.setTimeout(
          () => {

            normalizeBuyArea(
              document
            );

          },
          0
        );

      }
    );

  }



  /* ==========================================================
     DOM SICHERHEITSNETZ

     Shopify kann beim Variantenwechsel
     Teile des Produktbereiches ersetzen.

     Deshalb wird nach echten DOM-Änderungen
     erneut geprüft.

     Es wird aber nur verschoben,
     wenn die Reihenfolge tatsächlich
     falsch ist.
     ========================================================== */

  if (
    typeof MutationObserver !==
    'undefined'
  ) {

    let scheduled =
      false;


    const observer =
      new MutationObserver(
        () => {

          if (scheduled) {
            return;
          }


          scheduled =
            true;


          window.requestAnimationFrame(
            () => {

              scheduled =
                false;


              normalizeBuyArea(
                document
              );

            }
          );

        }
      );


    const startObserver =
      () => {

        if (!document.body) {
          return;
        }


        observer.observe(
          document.body,
          {
            childList: true,
            subtree: true
          }
        );

      };


    if (document.body) {

      startObserver();

    } else {

      document.addEventListener(
        'DOMContentLoaded',
        startObserver,
        {
          once: true
        }
      );

    }
  }

})();